// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc */
/* eslint-disable ta-code-conventions/has-matching-test */
import * as React from 'react';
import { connect } from 'react-redux';
import { Icon } from '@ta/styleguide.icon';
import classNames from '@ta/common.classnames';
import { localize } from '@ta/common.i18n';
import styles from './FacebookConnectButton.less';

/**
 * This is a 'Connect to Facebook' button component for the logged-in use case.
 * This button DOES NOT register new users, and can only be used for the purpose of connecting a facebook account to an
 * already logged-in TA user's account.
 * Clicking the button prompts a facebook window to pop up.  Once the user finishes facebook authentication, the
 * account will be facebook-connected (assuming no error screens show) and either the onComplete or onClose function will be called.
 */

type Props = {
  /**
   * Called when the RegOverlay for facebook-connect is closed in some way, whether successful or otherwise.
   */
  onClose?: () => void,
  /**
   * Called when the user clicks the 'Connect to Facebook' button.
   */
  onClick?: (e: SyntheticEvent<*>) => void,
  /**
   * Called if there is a failure during facebook authentication.
   * NOT called if there is an error connecting the account post-facebook-authentication.
   */
  onFbAuthFailure?: () => void,
  fullwidth?: boolean,
  taServerTime: number,
  facebookConnectAppId: string,
  skipFacebookSessionCheck: boolean,
  apiVersion: string,
  sdkUrl: string,
  facebookPermissions: string,
  viewportCategory: string,
};

class FacebookConnectButton extends React.Component<Props> {
  static defaultProps = {
    onClose: undefined,
    onClick: undefined,
    onFbAuthFailure: undefined,
    fullwidth: false,
  };

  componentDidMount() {
    // require FB.js on mount, so initialization of FB is completed by the time the button is clicked.
    // eslint-disable-next-line no-restricted-globals
    window.require(['trjs!ta/registration/FB'], () => {});
  }

  onClick = (e: SyntheticEvent<*>) => {
    this.connectToFB();
    if (this.props.onClick) {
      this.props.onClick(e);
    }
  };

  /**
   * Connects the logged-in user's TA account to their Facebook account.
   * See https://grok.dev.tripadvisor.com/xref/mainline/site/js3_secure/ssl/ta/registration/FB.js#325
   */
  connectToFB = () => {
    // eslint-disable-next-line no-restricted-globals
    window.require(['trjs!ta/registration/FB'], taFB => {
      taFB.showLogin(
        // success function
        (authResponse, expires) => {
          // eslint-disable-next-line no-restricted-globals
          window.require(['ta/registration/RegOverlay'], regOverlay => {
            const isMobile = this.props.viewportCategory === 'MOBILE';
            regOverlay.showRegOverlay(null, null, isMobile, {
              flow: 'FACEBOOK_CONNECT',
              /* unique pid for the facebook connect member.
                 Note: this does not get attached to the member in the t_member table.
                 It is only used for logging purposes. */
              pid: 40466,
              userRequestedForce: 'true',
              extraQueryParams: {
                token: authResponse.accessToken,
                expires,
                social: 'true',
              },
              onClose: () => {
                if (this.props.onClose) {
                  this.props.onClose();
                }
              },
            });
          });
        },
        // fail function
        () => {
          if (this.props.onFbAuthFailure) {
            this.props.onFbAuthFailure();
          }
        },
      );
    });
  };

  render() {
    return (
      <React.Fragment>
        <div
          id="fb_context_data"
          className="hidden"
          data-ta-server-time={this.props.taServerTime}
          data-app-id={this.props.facebookConnectAppId}
          data-skip-session-check={this.props.skipFacebookSessionCheck}
          data-api-version={this.props.apiVersion}
          data-facebook-permissions={this.props.facebookPermissions}
        />
        <div id="fb_sdk_url" data-url={this.props.sdkUrl} />
        <div className={styles.buttonContainer}>
          <button
            className={classNames(
              'ui_button',
              { fullwidth: this.props.fullwidth },
              styles.facebook_ui_button,
            )}
            onClick={this.onClick}
          >
            <Icon className={styles.icon} name="facebook" />
            <span>
              {/* Continue with Facebook */}
              {localize('reg_continue_w_face')}
            </span>
          </button>
        </div>
      </React.Fragment>
    );
  }
}

type OwnProps = {|
  /**
   * Called when the RegOverlay for facebook-connect is closed in some way, whether successful or otherwise.
   */
  onClose?: () => void,
  /**
   * Called when the user clicks the 'Connect to Facebook' button.
   */
  onClick?: (e: SyntheticEvent<*>) => void,

  /**
   * Called if there is a failure during facebook authentication.
   * NOT called if there is an error connecting the account post-facebook-authentication.
   */
  onFbAuthFailure?: () => void,
  fullwidth?: boolean,
|};

export default connect<Props, OwnProps, _, _, _, _>((state, ownProps: OwnProps) => ({
  taServerTime: state.auth.fbApi.taServerTime,
  facebookConnectAppId: state.auth.fbApi.facebookConnectAppId,
  skipFacebookSessionCheck: state.auth.fbApi.skipFacebookSessionCheck,
  apiVersion: state.auth.fbApi.apiVersion,
  sdkUrl: state.auth.fbApi.sdkUrl,
  facebookPermissions: state.auth.fbApi.facebookPermissions,
  viewportCategory: state.meta.device.viewportCategory,
  ...ownProps,
}))(FacebookConnectButton);
